import { initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';

// Firebase 설정 객체
// Vite에서는 import.meta.env를 사용해야 합니다
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "AIzaSyDgSY_Vtv7r7oIDsW3JEz9gLkF7_F6Njfo",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "jalam-50cbd.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "jalam-50cbd",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "jalam-50cbd.appspot.com",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "763935777700",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:763935777700:web:b5820249d58e4ce3555731",
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || "G-N3P74QE4MP"
};

// Apple 로그인 설정
// 참고: Firebase를 사용할 경우 Service ID는 Firebase Console에 등록해야 함
// Service ID: com.jalam.react.login
// Firebase Console > Authentication > Sign-in method > Apple에 'com.jalam.react.login' 등록 필요
// 실제로는 Firebase Console 설정을 사용하므로 코드에서 직접 설정하지 않음
export const appleConfig = {
  clientId: 'com.jalam.react.login', // Service ID (참고용)
  redirectURI: window.location.origin,
  scope: 'name email',
  state: 'apple_signin',
  responseType: 'code id_token',
  responseMode: 'form_post',
  usePopup: true
};

// 환경변수 사용 여부 확인

// Firebase 앱 초기화
const app = initializeApp(firebaseConfig);

// App Check — 다른 Firebase 서비스보다 먼저 켜야 첫 요청부터 토큰이 붙는다.
// 사이트 키(Firebase 콘솔 App Check 에 등록한 reCAPTCHA Enterprise 키)가 없으면
// 켜지 않는다. 키 없이 켜면 토큰 발급이 실패해 강제 적용 시 요청이 막힌다.
const appCheckSiteKey = import.meta.env.VITE_RECAPTCHA_ENTERPRISE_SITE_KEY;
if (appCheckSiteKey) {
  if (import.meta.env.DEV) {
    // 로컬 개발: 콘솔에 찍히는 디버그 토큰을 App Check > 디버그 토큰에 등록해 쓴다.
    (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean | string }).FIREBASE_APPCHECK_DEBUG_TOKEN =
      import.meta.env.VITE_APPCHECK_DEBUG_TOKEN || true;
  }
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

// Firebase 서비스들 초기화
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// 개발 환경에서 에뮬레이터 연결 (선택사항)
if (import.meta.env.DEV && import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true') {
  try {
    // Auth 에뮬레이터 연결
    if (!auth.emulatorConfig) {
      connectAuthEmulator(auth, 'http://localhost:9099');
    }
    
    // Firestore 에뮬레이터 연결
    connectFirestoreEmulator(db, 'localhost', 8080);
    
    // Storage 에뮬레이터 연결
    connectStorageEmulator(storage, 'localhost', 9199);
    
  } catch (error) {
    console.warn('⚠️ Firebase 에뮬레이터 연결 실패:', error);
  }
}


// Firebase 앱 인스턴스 export
export default app;

// 환경변수 사용 상태 확인 함수
export const getEnvironmentInfo = () => {
  return {
    nodeEnv: import.meta.env.MODE,
    useEmulator: import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true',
    appName: import.meta.env.VITE_APP_NAME || '사장님 가계부',
    version: import.meta.env.VITE_APP_VERSION || '1.0.0',
    firebaseConfig: {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY ? '환경변수 사용' : '기본값 사용',
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ? '환경변수 사용' : '기본값 사용',
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ? '환경변수 사용' : '기본값 사용',
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ? '환경변수 사용' : '기본값 사용',
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ? '환경변수 사용' : '기본값 사용',
      appId: import.meta.env.VITE_FIREBASE_APP_ID ? '환경변수 사용' : '기본값 사용',
      measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID ? '환경변수 사용' : '기본값 사용'
    }
  };
};

// Firebase 설정 검증 함수
export const validateFirebaseConfig = (): boolean => {
  const requiredFields = [
    'apiKey',
    'authDomain', 
    'projectId',
    'storageBucket',
    'messagingSenderId',
    'appId'
  ];
  
  const missingFields = requiredFields.filter(field => 
    !firebaseConfig[field as keyof typeof firebaseConfig] || 
    firebaseConfig[field as keyof typeof firebaseConfig] === `your-${field.replace(/([A-Z])/g, '-$1').toLowerCase()}`
  );
  
  if (missingFields.length > 0) {
    console.error('❌ Firebase 설정이 완료되지 않았습니다. 다음 필드들을 확인해주세요:', missingFields);
    return false;
  }
  
  return true;
};

// Firebase 연결 상태 확인
export const checkFirebaseConnection = async (): Promise<boolean> => {
  try {
    // 간단한 Firestore 연결 테스트

    return true;
  } catch (error) {
    console.error('❌ Firebase 연결 실패:', error);
    return false;
  }
};
